// src/pages/ClientSocialPagesPage.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, agencyFunctions } from '../firebase'; // Adjust path
import { useAuth } from '../context/AuthContext';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { getPlatformLabel } from '../utils/socialEnumLabels';
import styles from '../styles/ClientSocialPagesPage.module.css';

const TRANSLATIONS = {
    en: {
        pageTitle: 'Client Social Pages',
        noPermission: 'You do not have permission to view this page.',
        searchClientLabel: 'Search Client',
        searchClientPlaceholder: 'Search clients...',
        clientLabel: 'Client',
        allClients: 'All Clients',
        unnamed: 'Unnamed',
        addPage: '+ Add Page',
        cancel: 'Cancel',
        addSocialPageFor: 'Add Social Page for',
        platformLabel: 'Platform',
        pageNameLabel: 'Page Name / Handle',
        pageUrlLabel: 'Page URL',
        monthlyTargetLabel: 'Monthly Target',
        notesLabel: 'Notes',
        adding: 'Adding...',
        addPageButton: 'Add Page',
        loadingPages: 'Loading social pages...',
        colPlatform: 'Platform',
        colPage: 'Page',
        colClient: 'Client',
        colProgress: 'Progress',
        colNotes: 'Notes',
        colActions: 'Actions',
        noPagesForClient: 'No social pages found for this client.',
        noPagesFound: 'No social pages found.',
        unknownClient: 'Unknown Client',
        notAvailable: 'N/A',
        thisMonth: 'this month',
        edit: 'Edit',
        deleting: 'Deleting...',
        delete: 'Delete',
        viewOnly: 'View only',
        saving: 'Saving...',
        save: 'Save',
        selectClientFirst: 'Please select a client before adding a page.',
        pageNameRequired: 'Page name is required.',
        failedToAddPage: 'Failed to add social page. Please try again.',
        failedToUpdatePage: 'Failed to update social page. Please try again.',
        failedToLoadClients: 'Failed to load clients.',
        failedToLoadPages: 'Failed to load social pages.',
        failedToDeletePage: 'Failed to delete {name}. Please try again.',
        defaultPage: 'page',
        confirmDeletePage: 'Are you sure you want to delete the page "{name}"?\nThis action cannot be undone.',
        editTitle: 'Edit',
        deleteTitle: 'Delete',
        metaConnected: 'Connected',
        metaNotConnected: 'Not Connected',
        metaConnectionTitle: 'Meta Connection (Facebook / Instagram)',
        fbPageIdLabel: 'Facebook Page ID',
        igUserIdLabel: 'Instagram Business Account ID',
        pageAccessTokenLabel: 'Page Access Token',
        pageAccessTokenPlaceholder: 'Paste the System User Page Access Token',
        pageAccessTokenHint: 'Leave blank to keep the current token; only fill in to set or replace it. From Business Settings → System Users (graph.facebook.com).',
        igAccessTokenLabel: 'Instagram Access Token',
        igAccessTokenPlaceholder: 'Paste the Instagram User Access Token',
        igAccessTokenHint: 'Leave blank to keep the current token; only fill in to set or replace it. From the Meta Developers Console → Instagram API with Instagram Login → Create Token (graph.instagram.com). This is NOT a Facebook Page token.',
        connectButton: 'Save Connection',
        connecting: 'Connecting...',
        disconnectButton: 'Disconnect',
        disconnecting: 'Disconnecting...',
        fbPageIdRequired: 'Facebook Page ID is required.',
        pageAccessTokenRequired: 'A Page Access Token is required to connect.',
        igUserIdRequired: 'Instagram Business Account ID is required.',
        igAccessTokenRequired: 'An Instagram Access Token is required to connect.',
        metaSaveFailed: 'Failed to save the Meta connection. Please check the token and try again.',
        metaDisconnectFailed: 'Failed to disconnect the Meta connection. Please try again.',
        metaDisconnectConfirm: 'Disconnect the Meta connection for "{name}"? Automatic and manual publishing will stop working until reconnected.',
        connectFacebookButton: 'Connect with Facebook',
        connectInstagramButton: 'Connect with Instagram',
        oauthRedirecting: 'Redirecting...',
        oauthStartFailed: 'Could not start the login flow. Please try again.',
        oauthPickerTitle: 'Choose the Facebook Page to connect',
        oauthPickerEmpty: "No Pages were found on this Facebook account. Make sure you're an admin of the Page, then try again.",
        oauthPickerConnect: 'Connect this Page',
        oauthPickerCancel: 'Cancel',
        oauthConnectFailed: 'Could not connect the selected Page. Please try again.',
        oauthSuccessFacebook: 'Facebook Page connected successfully!',
        oauthSuccessInstagram: 'Instagram account "{username}" connected successfully!',
        oauthError: 'Login failed: {message}',
        manualEntryToggleShow: 'Advanced: enter Page ID / token manually',
        manualEntryToggleHide: 'Hide manual entry',
    },
    ar: {
        pageTitle: 'صفحات السوشيال ميديا للعملاء',
        noPermission: 'ليس لديك صلاحية لعرض هذه الصفحة.',
        searchClientLabel: 'بحث عن عميل',
        searchClientPlaceholder: 'بحث عن عملاء...',
        clientLabel: 'العميل',
        allClients: 'جميع العملاء',
        unnamed: 'بدون اسم',
        addPage: '+ إضافة صفحة',
        cancel: 'إلغاء',
        addSocialPageFor: 'إضافة صفحة سوشيال ميديا لـ',
        platformLabel: 'المنصة',
        pageNameLabel: 'اسم الصفحة / المعرّف',
        pageUrlLabel: 'رابط الصفحة',
        monthlyTargetLabel: 'الهدف الشهري',
        notesLabel: 'ملاحظات',
        adding: 'جاري الإضافة...',
        addPageButton: 'إضافة صفحة',
        loadingPages: 'جاري تحميل صفحات السوشيال ميديا...',
        colPlatform: 'المنصة',
        colPage: 'الصفحة',
        colClient: 'العميل',
        colProgress: 'التقدم',
        colNotes: 'ملاحظات',
        colActions: 'الإجراءات',
        noPagesForClient: 'لا توجد صفحات سوشيال ميديا لهذا العميل.',
        noPagesFound: 'لا توجد صفحات سوشيال ميديا.',
        unknownClient: 'عميل غير معروف',
        notAvailable: 'غير متاح',
        thisMonth: 'هذا الشهر',
        edit: 'تعديل',
        deleting: 'جاري الحذف...',
        delete: 'حذف',
        viewOnly: 'عرض فقط',
        saving: 'جاري الحفظ...',
        save: 'حفظ',
        selectClientFirst: 'يرجى اختيار عميل قبل إضافة صفحة.',
        pageNameRequired: 'اسم الصفحة مطلوب.',
        failedToAddPage: 'فشل إضافة صفحة السوشيال ميديا. حاول مرة أخرى.',
        failedToUpdatePage: 'فشل تحديث صفحة السوشيال ميديا. حاول مرة أخرى.',
        failedToLoadClients: 'فشل تحميل العملاء.',
        failedToLoadPages: 'فشل تحميل صفحات السوشيال ميديا.',
        failedToDeletePage: 'فشل حذف {name}. حاول مرة أخرى.',
        defaultPage: 'الصفحة',
        confirmDeletePage: 'هل أنت متأكد أنك تريد حذف الصفحة "{name}"؟\nلا يمكن التراجع عن هذا الإجراء.',
        editTitle: 'تعديل',
        deleteTitle: 'حذف',
        metaConnected: 'متصل',
        metaNotConnected: 'غير متصل',
        metaConnectionTitle: 'ربط ميتا (فيسبوك / انستغرام)',
        fbPageIdLabel: 'رقم صفحة فيسبوك (Page ID)',
        igUserIdLabel: 'رقم حساب انستغرام التجاري',
        pageAccessTokenLabel: 'رمز وصول الصفحة (Access Token)',
        pageAccessTokenPlaceholder: 'الصق رمز وصول الصفحة (System User Token)',
        pageAccessTokenHint: 'اتركه فارغاً للحفاظ على الرمز الحالي؛ عبّئه فقط لتعيين رمز جديد أو استبداله. من Business Settings ← System Users (graph.facebook.com).',
        igAccessTokenLabel: 'رمز وصول انستغرام',
        igAccessTokenPlaceholder: 'الصق رمز وصول مستخدم انستغرام',
        igAccessTokenHint: 'اتركه فارغاً للحفاظ على الرمز الحالي؛ عبّئه فقط لتعيين رمز جديد أو استبداله. من Meta Developers Console ← Instagram API with Instagram Login ← Create Token (graph.instagram.com). هذا ليس رمز صفحة فيسبوك.',
        connectButton: 'حفظ الربط',
        connecting: 'جاري الربط...',
        disconnectButton: 'فصل الربط',
        disconnecting: 'جاري الفصل...',
        fbPageIdRequired: 'رقم صفحة فيسبوك مطلوب.',
        pageAccessTokenRequired: 'رمز وصول الصفحة مطلوب لإكمال الربط.',
        igUserIdRequired: 'رقم حساب انستغرام التجاري مطلوب.',
        igAccessTokenRequired: 'رمز وصول انستغرام مطلوب لإكمال الربط.',
        metaSaveFailed: 'فشل حفظ ربط ميتا. تحقق من الرمز وحاول مرة أخرى.',
        metaDisconnectFailed: 'فشل فصل ربط ميتا. حاول مرة أخرى.',
        metaDisconnectConfirm: 'فصل ربط ميتا لـ "{name}"؟ سيتوقف النشر التلقائي والنشر اليدوي عن العمل حتى إعادة الربط.',
        connectFacebookButton: 'تسجيل الدخول بفيسبوك',
        connectInstagramButton: 'تسجيل الدخول بانستغرام',
        oauthRedirecting: 'جاري التوجيه...',
        oauthStartFailed: 'تعذر بدء عملية تسجيل الدخول. حاول مرة أخرى.',
        oauthPickerTitle: 'اختر صفحة فيسبوك المراد ربطها',
        oauthPickerEmpty: 'لم يتم العثور على أي صفحة في حساب فيسبوك هذا. تأكد من أنك مسؤول (admin) على الصفحة وحاول مرة أخرى.',
        oauthPickerConnect: 'ربط هذه الصفحة',
        oauthPickerCancel: 'إلغاء',
        oauthConnectFailed: 'تعذر ربط الصفحة المختارة. حاول مرة أخرى.',
        oauthSuccessFacebook: 'تم ربط صفحة فيسبوك بنجاح!',
        oauthSuccessInstagram: 'تم ربط حساب انستغرام "{username}" بنجاح!',
        oauthError: 'فشل تسجيل الدخول: {message}',
        manualEntryToggleShow: 'متقدم: أدخل رقم الصفحة / الرمز يدوياً',
        manualEntryToggleHide: 'إخفاء الإدخال اليدوي',
    },
};

const PLATFORM_OPTIONS = ['Facebook', 'Instagram', 'TikTok', 'LinkedIn', 'X', 'YouTube', 'WhatsApp Channel'];
const META_PLATFORMS = ['Facebook', 'Instagram'];
const EMPTY_META_FORM = { fbPageId: '', igUserId: '', pageAccessToken: '', igAccessToken: '' };

// Roles allowed to view this page at all
const PAGE_ACCESS_ROLES = ['Manager', 'Accounts Manager', 'Sales', 'Content Creator'];
// Roles allowed to add/edit/delete a social page
const MANAGE_ROLES = ['Manager', 'Accounts Manager', 'Sales'];

const EMPTY_FORM = {
    platform: PLATFORM_OPTIONS[0],
    pageName: '',
    pageUrl: '',
    monthlyTarget: '',
    notes: '',
};

const ClientSocialPagesPage = () => {
    const { currentUser, userRole } = useAuth();
    const { language, toggleLanguage, isRTL } = useSocialLanguage();
    const t = TRANSLATIONS[language];
    const canManage = MANAGE_ROLES.includes(userRole);
    const [searchParams, setSearchParams] = useSearchParams();

    const [clients, setClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(true);
    const [selectedClientId, setSelectedClientId] = useState('');
    const [clientSearch, setClientSearch] = useState('');

    const [pages, setPages] = useState([]);
    const [loadingPages, setLoadingPages] = useState(true);
    const [error, setError] = useState('');

    const [showAddForm, setShowAddForm] = useState(false);
    const [addForm, setAddForm] = useState(EMPTY_FORM);
    const [savingAdd, setSavingAdd] = useState(false);

    const [editingPageId, setEditingPageId] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_FORM);
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [metaForm, setMetaForm] = useState(EMPTY_META_FORM);
    const [savingMeta, setSavingMeta] = useState(false);
    const [disconnectingMetaId, setDisconnectingMetaId] = useState(null);
    const [manualEntryOpenIds, setManualEntryOpenIds] = useState(() => new Set());

    // "Connect with Facebook / Instagram" OAuth flow
    const [oauthBusyPageId, setOauthBusyPageId] = useState(null);
    const [oauthPicker, setOauthPicker] = useState(null); // { sessionId, pageId, pages }
    const [connectingFbPageId, setConnectingFbPageId] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Map of pageId -> { count, target } progress info for "this month"
    const [progressMap, setProgressMap] = useState(new Map());
    const [loadingProgress, setLoadingProgress] = useState(false);

    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Fetch all clients for the picker
    useEffect(() => {
        const fetchClients = async () => {
            setLoadingClients(true);
            try {
                const clientsCol = collection(db, 'clients');
                const q = query(clientsCol, orderBy('name', 'asc'));
                const snapshot = await getDocs(q);
                if (!isMounted.current) return;
                setClients(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (err) {
                console.error('Error fetching clients:', err);
                if (isMounted.current) setError('failedToLoadClients');
            } finally {
                if (isMounted.current) setLoadingClients(false);
            }
        };
        fetchClients();
    }, []);

    // Fetch socialPages, real-time. Filtered by selectedClientId when set.
    useEffect(() => {
        setLoadingPages(true);
        setError('');
        const pagesCol = collection(db, 'socialPages');
        const q = selectedClientId
            ? query(pagesCol, where('clientId', '==', selectedClientId))
            : query(pagesCol);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!isMounted.current) return;
            const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            setPages(list);
            setLoadingPages(false);
        }, (err) => {
            if (!isMounted.current) return;
            console.error('Error fetching social pages:', err);
            setError('failedToLoadPages');
            setLoadingPages(false);
        });

        return () => unsubscribe();
    }, [selectedClientId]);

    // For each loaded page, compute "this month's progress" via getDocs on contentTasks
    useEffect(() => {
        if (pages.length === 0) {
            setProgressMap(new Map());
            return;
        }

        let cancelled = false;
        const computeProgress = async () => {
            setLoadingProgress(true);
            const now = new Date();
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);

            const newMap = new Map();
            try {
                await Promise.all(pages.map(async (page) => {
                    const tasksCol = collection(db, 'contentTasks');
                    const q = query(
                        tasksCol,
                        where('pageId', '==', page.id),
                        where('status', '==', 'Published')
                    );
                    const snapshot = await getDocs(q);
                    let count = 0;
                    snapshot.docs.forEach((d) => {
                        const data = d.data();
                        const publishedDate = data.publishedDate?.toDate ? data.publishedDate.toDate() : null;
                        if (publishedDate && publishedDate >= monthStart && publishedDate < monthEnd) {
                            count += 1;
                        }
                    });
                    newMap.set(page.id, count);
                }));
            } catch (err) {
                console.error('Error computing monthly progress:', err);
            }

            if (!cancelled && isMounted.current) {
                setProgressMap(newMap);
                setLoadingProgress(false);
            }
        };

        computeProgress();
        return () => { cancelled = true; };
    }, [pages]);

    // Handle the redirect back from Meta's OAuth dialog (facebookOAuthCallback /
    // instagramOAuthCallback land here with ?oauthSession=<id>&platform=... or ?oauthError=...).
    useEffect(() => {
        const sessionId = searchParams.get('oauthSession');
        const oauthErrorParam = searchParams.get('oauthError');

        if (!sessionId && !oauthErrorParam) return;

        const clearParams = () => {
            const next = new URLSearchParams(searchParams);
            next.delete('oauthSession');
            next.delete('oauthError');
            next.delete('platform');
            setSearchParams(next, { replace: true });
        };

        if (oauthErrorParam) {
            setError(t.oauthError.replace('{message}', oauthErrorParam));
            clearParams();
            return;
        }

        const resolveSession = async () => {
            setError('');
            try {
                const getOAuthSessionResult = httpsCallable(agencyFunctions, 'getOAuthSessionResult');
                const res = await getOAuthSessionResult({ sessionId });
                const result = res.data;
                if (result.platform === 'Facebook') {
                    if (!result.pages || result.pages.length === 0) {
                        setError(t.oauthPickerEmpty);
                    } else {
                        setOauthPicker({ sessionId, pageId: result.pageId, pages: result.pages });
                    }
                } else {
                    setSuccessMessage(t.oauthSuccessInstagram.replace('{username}', result.igUsername || ''));
                }
            } catch (err) {
                console.error('Error resolving OAuth session:', err);
                setError(t.oauthError.replace('{message}', err.message || ''));
            } finally {
                clearParams();
            }
        };
        resolveSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleConnectFacebook = useCallback(async (pageId) => {
        setError('');
        setOauthBusyPageId(pageId);
        try {
            const startFacebookOAuth = httpsCallable(agencyFunctions, 'startFacebookOAuth');
            const res = await startFacebookOAuth({ pageId });
            window.location.href = res.data.url;
        } catch (err) {
            console.error('Error starting Facebook OAuth:', err);
            setError(err.message || t.oauthStartFailed);
            setOauthBusyPageId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t.oauthStartFailed]);

    const handleConnectInstagram = useCallback(async (pageId) => {
        setError('');
        setOauthBusyPageId(pageId);
        try {
            const startInstagramOAuth = httpsCallable(agencyFunctions, 'startInstagramOAuth');
            const res = await startInstagramOAuth({ pageId });
            window.location.href = res.data.url;
        } catch (err) {
            console.error('Error starting Instagram OAuth:', err);
            setError(err.message || t.oauthStartFailed);
            setOauthBusyPageId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t.oauthStartFailed]);

    const handlePickFacebookPage = async (fbPageId) => {
        if (!oauthPicker) return;
        setError('');
        setConnectingFbPageId(fbPageId);
        try {
            const connectDiscoveredFacebookPage = httpsCallable(agencyFunctions, 'connectDiscoveredFacebookPage');
            await connectDiscoveredFacebookPage({ sessionId: oauthPicker.sessionId, fbPageId });
            setSuccessMessage(t.oauthSuccessFacebook);
            setOauthPicker(null);
        } catch (err) {
            console.error('Error connecting Facebook page:', err);
            setError(err.message || t.oauthConnectFailed);
        } finally {
            setConnectingFbPageId(null);
        }
    };

    const toggleManualEntry = (pageId) => {
        setManualEntryOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(pageId)) next.delete(pageId); else next.add(pageId);
            return next;
        });
    };

    const filteredClients = useMemo(() => {
        if (!clientSearch.trim()) return clients;
        const term = clientSearch.trim().toLowerCase();
        return clients.filter((c) => c.name?.toLowerCase().includes(term));
    }, [clients, clientSearch]);

    const selectedClient = useMemo(
        () => clients.find((c) => c.id === selectedClientId) || null,
        [clients, selectedClientId]
    );

    const pagesByClientName = useMemo(() => {
        if (selectedClientId) return null; // not needed when filtering by a single client
        const groups = new Map();
        pages.forEach((page) => {
            const key = page.clientName || t.unknownClient;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(page);
        });
        return groups;
    }, [pages, selectedClientId, t.unknownClient]);

    const resetAddForm = () => setAddForm(EMPTY_FORM);

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!selectedClient) {
            setError(t.selectClientFirst);
            return;
        }
        if (!addForm.pageName.trim()) {
            setError(t.pageNameRequired);
            return;
        }
        setSavingAdd(true);
        try {
            const pagesCol = collection(db, 'socialPages');
            await addDoc(pagesCol, {
                clientId: selectedClient.id,
                clientName: selectedClient.name || '',
                platform: addForm.platform,
                pageName: addForm.pageName.trim(),
                pageUrl: addForm.pageUrl.trim(),
                monthlyTarget: addForm.monthlyTarget === '' ? 0 : Number(addForm.monthlyTarget),
                notes: addForm.notes.trim(),
                createdBy: currentUser?.uid || null,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            resetAddForm();
            setShowAddForm(false);
        } catch (err) {
            console.error('Error adding social page:', err);
            setError(t.failedToAddPage);
        } finally {
            setSavingAdd(false);
        }
    };

    const startEdit = (page) => {
        setEditingPageId(page.id);
        setEditForm({
            platform: page.platform || PLATFORM_OPTIONS[0],
            pageName: page.pageName || '',
            pageUrl: page.pageUrl || '',
            monthlyTarget: page.monthlyTarget ?? '',
            notes: page.notes || '',
        });
        setMetaForm({
            fbPageId: page.fbPageId || '',
            igUserId: page.igUserId || '',
            pageAccessToken: '',
            igAccessToken: '',
        });
    };

    const cancelEdit = () => {
        setEditingPageId(null);
        setEditForm(EMPTY_FORM);
        setMetaForm(EMPTY_META_FORM);
    };

    const handleSaveMetaConnection = async (pageId, platform) => {
        setError('');
        const payload = { pageId };
        if (platform === 'Facebook') {
            if (!metaForm.fbPageId.trim()) { setError(t.fbPageIdRequired); return; }
            if (!metaForm.pageAccessToken.trim()) { setError(t.pageAccessTokenRequired); return; }
            payload.fbPageId = metaForm.fbPageId.trim();
            payload.pageAccessToken = metaForm.pageAccessToken.trim();
        } else if (platform === 'Instagram') {
            if (!metaForm.igUserId.trim()) { setError(t.igUserIdRequired); return; }
            if (!metaForm.igAccessToken.trim()) { setError(t.igAccessTokenRequired); return; }
            payload.igUserId = metaForm.igUserId.trim();
            payload.igAccessToken = metaForm.igAccessToken.trim();
        } else {
            return;
        }
        setSavingMeta(true);
        try {
            const saveMetaPageCredentials = httpsCallable(agencyFunctions, 'saveMetaPageCredentials');
            await saveMetaPageCredentials(payload);
            setMetaForm((f) => ({ ...f, pageAccessToken: '', igAccessToken: '' }));
        } catch (err) {
            console.error('Error saving Meta connection:', err);
            setError(err.message || t.metaSaveFailed);
        } finally {
            setSavingMeta(false);
        }
    };

    const handleDisconnectMeta = async (pageId, pageName) => {
        const confirmMsg = t.metaDisconnectConfirm.replace('{name}', pageName || t.defaultPage);
        if (!window.confirm(confirmMsg)) return;
        setError('');
        setDisconnectingMetaId(pageId);
        try {
            const disconnectMetaPage = httpsCallable(agencyFunctions, 'disconnectMetaPage');
            await disconnectMetaPage({ pageId });
        } catch (err) {
            console.error('Error disconnecting Meta page:', err);
            setError(t.metaDisconnectFailed);
        } finally {
            setDisconnectingMetaId(null);
        }
    };

    const handleEditSubmit = async (e, pageId) => {
        e.preventDefault();
        setError('');
        if (!editForm.pageName.trim()) {
            setError(t.pageNameRequired);
            return;
        }
        setSavingEdit(true);
        try {
            const pageDocRef = doc(db, 'socialPages', pageId);
            await updateDoc(pageDocRef, {
                platform: editForm.platform,
                pageName: editForm.pageName.trim(),
                pageUrl: editForm.pageUrl.trim(),
                monthlyTarget: editForm.monthlyTarget === '' ? 0 : Number(editForm.monthlyTarget),
                notes: editForm.notes.trim(),
                updatedAt: serverTimestamp(),
            });
            cancelEdit();
        } catch (err) {
            console.error('Error updating social page:', err);
            setError(t.failedToUpdatePage);
        } finally {
            setSavingEdit(false);
        }
    };

    const handleDelete = async (pageId, pageName) => {
        if (!canManage) return;
        const confirmMsg = t.confirmDeletePage.replace('{name}', pageName || t.defaultPage);
        if (window.confirm(confirmMsg)) {
            setDeletingId(pageId);
            setError('');
            try {
                await deleteDoc(doc(db, 'socialPages', pageId));
            } catch (err) {
                console.error('Error deleting social page:', err);
                setError(t.failedToDeletePage.replace('{name}', pageName || t.defaultPage));
            } finally {
                setDeletingId(null);
            }
        }
    };

    const renderProgress = (page) => {
        const count = progressMap.get(page.id);
        const target = page.monthlyTarget || 0;
        if (loadingProgress && count === undefined) {
            return <span className={styles.progressLoading}>...</span>;
        }
        const safeCount = count || 0;
        const met = target > 0 && safeCount >= target;
        return (
            <span className={`${styles.progressBadge} ${met ? styles.progressMet : ''}`}>
                {safeCount} / {target || '—'} {t.thisMonth}
            </span>
        );
    };

    const renderPageRow = (page) => {
        const isEditing = editingPageId === page.id;

        if (isEditing) {
            const colSpan = selectedClientId ? 5 : 6;
            return (
                <tr key={page.id} className={styles.editingRow}>
                    <td colSpan={colSpan}>
                        <form onSubmit={(e) => handleEditSubmit(e, page.id)} className={styles.inlineEditForm}>
                            <div className={styles.inlineFieldGroup}>
                                <label>{t.platformLabel}</label>
                                <select
                                    value={editForm.platform}
                                    onChange={(e) => setEditForm((f) => ({ ...f, platform: e.target.value }))}
                                    disabled={savingEdit}
                                >
                                    {PLATFORM_OPTIONS.map((p) => (
                                        <option key={p} value={p}>{getPlatformLabel(p, language)}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.inlineFieldGroup}>
                                <label>{t.pageNameLabel}</label>
                                <input
                                    type="text"
                                    value={editForm.pageName}
                                    onChange={(e) => setEditForm((f) => ({ ...f, pageName: e.target.value }))}
                                    disabled={savingEdit}
                                    required
                                />
                            </div>
                            <div className={styles.inlineFieldGroup}>
                                <label>{t.pageUrlLabel}</label>
                                <input
                                    type="text"
                                    value={editForm.pageUrl}
                                    onChange={(e) => setEditForm((f) => ({ ...f, pageUrl: e.target.value }))}
                                    disabled={savingEdit}
                                />
                            </div>
                            <div className={styles.inlineFieldGroup}>
                                <label>{t.monthlyTargetLabel}</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={editForm.monthlyTarget}
                                    onChange={(e) => setEditForm((f) => ({ ...f, monthlyTarget: e.target.value }))}
                                    disabled={savingEdit}
                                />
                            </div>
                            <div className={styles.inlineFieldGroup}>
                                <label>{t.notesLabel}</label>
                                <textarea
                                    rows={2}
                                    value={editForm.notes}
                                    onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                                    disabled={savingEdit}
                                />
                            </div>
                            <div className={styles.inlineButtonGroup}>
                                <button type="button" className={styles.cancelButton} onClick={cancelEdit} disabled={savingEdit}>
                                    {t.cancel}
                                </button>
                                <button type="submit" className={styles.saveButton} disabled={savingEdit}>
                                    {savingEdit ? t.saving : t.save}
                                </button>
                            </div>
                        </form>

                        {META_PLATFORMS.includes(editForm.platform) && (
                            <div className={styles.metaConnectionBox}>
                                <h4 className={styles.metaConnectionTitle}>
                                    {t.metaConnectionTitle}
                                    <span className={page.metaConnected ? styles.metaConnectedBadge : styles.metaNotConnectedBadge}>
                                        {page.metaConnected ? t.metaConnected : t.metaNotConnected}
                                    </span>
                                </h4>

                                <div className={styles.inlineButtonGroup}>
                                    {editForm.platform === 'Facebook' ? (
                                        <button
                                            type="button"
                                            className={styles.saveButton}
                                            onClick={() => handleConnectFacebook(page.id)}
                                            disabled={oauthBusyPageId === page.id}
                                        >
                                            {oauthBusyPageId === page.id ? t.oauthRedirecting : t.connectFacebookButton}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className={styles.saveButton}
                                            onClick={() => handleConnectInstagram(page.id)}
                                            disabled={oauthBusyPageId === page.id}
                                        >
                                            {oauthBusyPageId === page.id ? t.oauthRedirecting : t.connectInstagramButton}
                                        </button>
                                    )}
                                    {page.metaConnected && (
                                        <button
                                            type="button"
                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                            onClick={() => handleDisconnectMeta(page.id, page.pageName)}
                                            disabled={disconnectingMetaId === page.id}
                                        >
                                            {disconnectingMetaId === page.id ? t.disconnecting : t.disconnectButton}
                                        </button>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    className={styles.cancelButton}
                                    onClick={() => toggleManualEntry(page.id)}
                                    style={{ marginTop: '8px', fontSize: '0.85em' }}
                                >
                                    {manualEntryOpenIds.has(page.id) ? t.manualEntryToggleHide : t.manualEntryToggleShow}
                                </button>

                                {manualEntryOpenIds.has(page.id) && (
                                <>
                                <div className={styles.inlineEditForm}>
                                    {editForm.platform === 'Facebook' ? (
                                        <>
                                            <div className={styles.inlineFieldGroup}>
                                                <label>{t.fbPageIdLabel}</label>
                                                <input
                                                    type="text"
                                                    value={metaForm.fbPageId}
                                                    onChange={(e) => setMetaForm((f) => ({ ...f, fbPageId: e.target.value }))}
                                                    disabled={savingMeta}
                                                />
                                            </div>
                                            <div className={styles.inlineFieldGroup}>
                                                <label>{t.pageAccessTokenLabel}</label>
                                                <input
                                                    type="password"
                                                    autoComplete="new-password"
                                                    value={metaForm.pageAccessToken}
                                                    onChange={(e) => setMetaForm((f) => ({ ...f, pageAccessToken: e.target.value }))}
                                                    placeholder={t.pageAccessTokenPlaceholder}
                                                    disabled={savingMeta}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className={styles.inlineFieldGroup}>
                                                <label>{t.igUserIdLabel}</label>
                                                <input
                                                    type="text"
                                                    value={metaForm.igUserId}
                                                    onChange={(e) => setMetaForm((f) => ({ ...f, igUserId: e.target.value }))}
                                                    disabled={savingMeta}
                                                />
                                            </div>
                                            <div className={styles.inlineFieldGroup}>
                                                <label>{t.igAccessTokenLabel}</label>
                                                <input
                                                    type="password"
                                                    autoComplete="new-password"
                                                    value={metaForm.igAccessToken}
                                                    onChange={(e) => setMetaForm((f) => ({ ...f, igAccessToken: e.target.value }))}
                                                    placeholder={t.igAccessTokenPlaceholder}
                                                    disabled={savingMeta}
                                                />
                                            </div>
                                        </>
                                    )}
                                    <div className={styles.inlineButtonGroup}>
                                        <button
                                            type="button"
                                            className={styles.saveButton}
                                            onClick={() => handleSaveMetaConnection(page.id, editForm.platform)}
                                            disabled={savingMeta}
                                        >
                                            {savingMeta ? t.connecting : t.connectButton}
                                        </button>
                                    </div>
                                </div>
                                <p className={styles.metaConnectionHint}>
                                    {editForm.platform === 'Facebook' ? t.pageAccessTokenHint : t.igAccessTokenHint}
                                </p>
                                </>
                                )}
                            </div>
                        )}
                    </td>
                </tr>
            );
        }

        return (
            <tr key={page.id} className={deletingId === page.id ? styles.deletingRow : ''}>
                <td data-label={`${t.colPlatform}:`}>
                    {page.platform ? getPlatformLabel(page.platform, language) : t.notAvailable}
                    {META_PLATFORMS.includes(page.platform) && (
                        <span className={page.metaConnected ? styles.metaConnectedBadge : styles.metaNotConnectedBadge}>
                            {page.metaConnected ? t.metaConnected : t.metaNotConnected}
                        </span>
                    )}
                </td>
                <td data-label={`${t.colPage}:`}>
                    {page.pageUrl ? (
                        <a href={page.pageUrl} target="_blank" rel="noopener noreferrer" className={styles.pageLink}>
                            {page.pageName || t.unnamed}
                        </a>
                    ) : (page.pageName || t.unnamed)}
                </td>
                {!selectedClientId && <td data-label={`${t.colClient}:`}>{page.clientName || t.notAvailable}</td>}
                <td data-label={`${t.colProgress}:`}>{renderProgress(page)}</td>
                <td data-label={`${t.colNotes}:`} className={styles.notesColumn}>{page.notes || '—'}</td>
                <td data-label={`${t.colActions}:`} className={styles.actionsCell}>
                    {canManage ? (
                        <>
                            <button
                                onClick={() => startEdit(page)}
                                className={`${styles.actionButton} ${styles.editButton}`}
                                title={`${t.editTitle} ${page.pageName || t.defaultPage}`}
                            >
                                {t.edit}
                            </button>
                            <button
                                onClick={() => handleDelete(page.id, page.pageName)}
                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                disabled={deletingId === page.id}
                                title={`${t.deleteTitle} ${page.pageName || t.defaultPage}`}
                            >
                                {deletingId === page.id ? t.deleting : t.delete}
                            </button>
                        </>
                    ) : (
                        <span className={styles.noActions}>{t.viewOnly}</span>
                    )}
                </td>
            </tr>
        );
    };

    if (!PAGE_ACCESS_ROLES.includes(userRole)) {
        return (
            <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
                <div className={styles.errorMessage}>{t.noPermission}</div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className={styles.header}>
                <h2 className={styles.title}>{t.pageTitle}</h2>
                <LanguageToggle language={language} onToggle={toggleLanguage} />
            </div>

            {error && <div className={styles.errorMessage}>{TRANSLATIONS[language][error] || error}</div>}
            {successMessage && (
                <div className={styles.errorMessage} style={{ background: '#dcfce7', color: '#166534', borderColor: '#86efac' }}>
                    {successMessage}
                </div>
            )}

            {oauthPicker && (
                <div className={styles.addFormBox}>
                    <h3 className={styles.addFormTitle}>{t.oauthPickerTitle}</h3>
                    <div className={styles.inlineButtonGroup} style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                        {oauthPicker.pages.map((p) => (
                            <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                <span>{p.name}</span>
                                <button
                                    type="button"
                                    className={styles.saveButton}
                                    onClick={() => handlePickFacebookPage(p.id)}
                                    disabled={connectingFbPageId !== null}
                                >
                                    {connectingFbPageId === p.id ? t.connecting : t.oauthPickerConnect}
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className={styles.buttonGroup}>
                        <button
                            type="button"
                            className={`${styles.formButton} ${styles.cancelButton}`}
                            onClick={() => setOauthPicker(null)}
                            disabled={connectingFbPageId !== null}
                        >
                            {t.oauthPickerCancel}
                        </button>
                    </div>
                </div>
            )}

            <div className={styles.clientPickerBox}>
                <div className={styles.inputGroup}>
                    <label htmlFor="clientSearch">{t.searchClientLabel}</label>
                    <input
                        type="text"
                        id="clientSearch"
                        placeholder={t.searchClientPlaceholder}
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        disabled={loadingClients}
                    />
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="clientSelect">{t.clientLabel}</label>
                    <select
                        id="clientSelect"
                        value={selectedClientId}
                        onChange={(e) => {
                            setSelectedClientId(e.target.value);
                            setShowAddForm(false);
                            cancelEdit();
                        }}
                        disabled={loadingClients}
                    >
                        <option value="">{t.allClients}</option>
                        {filteredClients.map((client) => (
                            <option key={client.id} value={client.id}>{client.name || t.unnamed}</option>
                        ))}
                    </select>
                </div>
                {selectedClient && canManage && (
                    <div className={styles.inputGroup}>
                        <label>&nbsp;</label>
                        <button
                            type="button"
                            className={styles.addButton}
                            onClick={() => setShowAddForm((v) => !v)}
                        >
                            {showAddForm ? t.cancel : t.addPage}
                        </button>
                    </div>
                )}
            </div>

            {showAddForm && selectedClient && canManage && (
                <div className={styles.addFormBox}>
                    <h3 className={styles.addFormTitle}>{t.addSocialPageFor} {selectedClient.name}</h3>
                    <form onSubmit={handleAddSubmit} className={styles.addForm}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="platform">{t.platformLabel}</label>
                            <select
                                id="platform"
                                value={addForm.platform}
                                onChange={(e) => setAddForm((f) => ({ ...f, platform: e.target.value }))}
                                disabled={savingAdd}
                            >
                                {PLATFORM_OPTIONS.map((p) => (
                                    <option key={p} value={p}>{getPlatformLabel(p, language)}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor="pageName">{t.pageNameLabel}</label>
                            <input
                                type="text"
                                id="pageName"
                                value={addForm.pageName}
                                onChange={(e) => setAddForm((f) => ({ ...f, pageName: e.target.value }))}
                                disabled={savingAdd}
                                required
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor="pageUrl">{t.pageUrlLabel}</label>
                            <input
                                type="text"
                                id="pageUrl"
                                value={addForm.pageUrl}
                                onChange={(e) => setAddForm((f) => ({ ...f, pageUrl: e.target.value }))}
                                disabled={savingAdd}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor="monthlyTarget">{t.monthlyTargetLabel}</label>
                            <input
                                type="number"
                                id="monthlyTarget"
                                min="0"
                                value={addForm.monthlyTarget}
                                onChange={(e) => setAddForm((f) => ({ ...f, monthlyTarget: e.target.value }))}
                                disabled={savingAdd}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor="notes">{t.notesLabel}</label>
                            <textarea
                                id="notes"
                                rows={3}
                                value={addForm.notes}
                                onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                                disabled={savingAdd}
                            />
                        </div>
                        <div className={styles.buttonGroup}>
                            <button
                                type="button"
                                className={`${styles.formButton} ${styles.cancelButton}`}
                                onClick={() => { setShowAddForm(false); resetAddForm(); }}
                                disabled={savingAdd}
                            >
                                {t.cancel}
                            </button>
                            <button type="submit" className={`${styles.formButton} ${styles.submitButton}`} disabled={savingAdd}>
                                {savingAdd ? t.adding : t.addPageButton}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {loadingPages ? (
                <div className={styles.loadingIndicator}>{t.loadingPages}</div>
            ) : selectedClientId ? (
                <div className={styles.tableContainer}>
                    <table className={styles.pagesTable}>
                        <thead className={styles.tableHeader}>
                            <tr>
                                <th>{t.colPlatform}</th>
                                <th>{t.colPage}</th>
                                <th>{t.colProgress}</th>
                                <th>{t.colNotes}</th>
                                <th className={styles.actionsHeader}>{t.colActions}</th>
                            </tr>
                        </thead>
                        <tbody className={styles.tableBody}>
                            {pages.length === 0 && (
                                <tr>
                                    <td colSpan="5" className={styles.noDataCell}>{t.noPagesForClient}</td>
                                </tr>
                            )}
                            {pages.map(renderPageRow)}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className={styles.groupedContainer}>
                    {pages.length === 0 && (
                        <div className={styles.noDataCell}>{t.noPagesFound}</div>
                    )}
                    {pagesByClientName && Array.from(pagesByClientName.entries()).map(([clientName, clientPages]) => (
                        <div key={clientName} className={styles.clientGroup}>
                            <h3 className={styles.clientGroupTitle}>{clientName}</h3>
                            <div className={styles.tableContainer}>
                                <table className={styles.pagesTable}>
                                    <thead className={styles.tableHeader}>
                                        <tr>
                                            <th>{t.colPlatform}</th>
                                            <th>{t.colPage}</th>
                                            <th>{t.colClient}</th>
                                            <th>{t.colProgress}</th>
                                            <th>{t.colNotes}</th>
                                            <th className={styles.actionsHeader}>{t.colActions}</th>
                                        </tr>
                                    </thead>
                                    <tbody className={styles.tableBody}>
                                        {clientPages.map(renderPageRow)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ClientSocialPagesPage;
